import { BaseStateInterface } from './basestate.state';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { QuestionnaireConfig } from '../../@types/dto/questionnaire-config.model';
import { QuestionnaireConfigHttpService } from '@services/datasources/questionnaireConfig-http.service';
import { Subject, takeUntil } from 'rxjs';

export interface QuestionnaireConfigModel extends BaseStateInterface {
    questionnaireConfig: QuestionnaireConfig | undefined;
}

// eslint-disable-next-line @typescript-eslint/no-namespace -- NGXS action grouping requires namespace for combined value+type semantics
export namespace QuestionnaireConfigAction {
    export class Activate {
        public static type = '[questionnaireConfig UI] Activate questionnaireConfig';

        constructor(
            public orgId: string,
            public entityId: string,
        ) {}
    }

    export class Deactivate {
        public static type = '[questionnaireConfig UI] Deactivate questionnaireConfig';
    }
}

@State<QuestionnaireConfigModel>({
    name: 'questionnaireConfig',
    defaults: {
        questionnaireConfig: undefined,
        isInit: false,
        isLoading: false,
        errors: [],
    },
})
@Injectable()
export class QuestionnaireConfigState {
    private readonly activateTeardown$ = new Subject<void>();

    constructor(private questionnaireConfigService: QuestionnaireConfigHttpService) {}

    //----- store action -----//

    @Action(QuestionnaireConfigAction.Activate)
    activate(ctx: StateContext<QuestionnaireConfigModel>, action: QuestionnaireConfigAction.Activate) {
        this.activateTeardown$.next();
        const untilCfg = this.activateTeardown$;
        this.questionnaireConfigService
            .get(action.orgId, action.entityId)
            .pipe(takeUntil(untilCfg))
            .subscribe((config) => {
                if (config) {
                    ctx.patchState({
                        questionnaireConfig: config,
                        isInit: true,
                        isLoading: false,
                        errors: undefined,
                    });
                } else {
                    const errors = ctx.getState().errors;
                    errors.push('Cannot fetch the questionnaire config');
                    ctx.patchState({
                        questionnaireConfig: undefined,
                        isInit: true,
                        isLoading: false,
                        errors: errors,
                    });
                }
            });
    }

    @Action(QuestionnaireConfigAction.Deactivate)
    deactivate(ctx: StateContext<QuestionnaireConfigModel>, _action: QuestionnaireConfigAction.Deactivate) {
        this.activateTeardown$.next();
        ctx.patchState({
            questionnaireConfig: undefined,
            isInit: false,
            isLoading: false,
            errors: [],
        });
    }

    @Selector([QuestionnaireConfigState])
    static getStaticConfig(state: QuestionnaireConfigModel) {
        return state.questionnaireConfig;
    }
}
